import {
  BackLink,
  EmptyState,
  ErrorState,
  LoadingState,
  WithHalfdot,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useCoverPublishing } from "~/components/covers/use-cover-publishing";
import { DashboardHighlightForm } from "~/components/highlights/DashboardHighlightForm";
import { Screen } from "~/components/ui/Screen";
import {
  ApiError,
  createDashboardHighlight,
  deleteDashboardHighlight,
  fetchAdminDashboardHighlight,
  updateDashboardHighlight,
} from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { DashboardHighlightAdminCreate } from "~/types/dashboard-highlights-admin";

function translateHighlightError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "highlightEditor.errors.slugTaken";
    if (error.status === 422) return "highlightEditor.errors.validation";
    return "common.somethingWentWrong";
  }
  return "highlightEditor.errors.network";
}

/** Create/edit route for current and historical Dashboard Highlights. */
export function DashboardHighlightEditorRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const isCreate = id === undefined;
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [savedOpen, setSavedOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const highlight = useQuery({
    queryKey: queryKeys.dashboardHighlight(id ?? "new"),
    queryFn: ({ signal }) => fetchAdminDashboardHighlight(id ?? "", signal),
    enabled: !isCreate,
  });

  // Making one row visible can hide another row in the same locale, so updates must
  // invalidate the whole resource rather than only the currently edited detail. The
  // cover library goes too: a save can change a cover's usage count.
  const invalidateAll = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardHighlightsRoot,
      }),
      queryClient.invalidateQueries({ queryKey: queryKeys.coversRoot }),
    ]);

  const create = useMutation({
    mutationFn: createDashboardHighlight,
    onSuccess: async () => {
      await invalidateAll();
      void navigate("/dashboard-highlights");
    },
    onError: (error) => setErrorKey(translateHighlightError(error)),
  });

  const update = useMutation({
    mutationFn: (body: DashboardHighlightAdminCreate) =>
      updateDashboardHighlight(id ?? "", body),
    onSuccess: async (updated) => {
      queryClient.setQueryData(
        queryKeys.dashboardHighlight(updated.id),
        updated,
      );
      await invalidateAll();
      setSavedOpen(true);
    },
    onError: (error) => setErrorKey(translateHighlightError(error)),
  });

  const remove = useMutation({
    mutationFn: () => deleteDashboardHighlight(id ?? ""),
    onSuccess: async () => {
      queryClient.removeQueries({
        queryKey: queryKeys.dashboardHighlight(id ?? ""),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardHighlightListsRoot,
      });
      void navigate("/dashboard-highlights");
    },
    onError: (error) => {
      setConfirmingDelete(false);
      setErrorKey(translateHighlightError(error));
    },
  });

  // Publishes into the library only; the form holds the URL and the save commits it.
  const covers = useCoverPublishing(setErrorKey);

  const busy =
    create.isPending || update.isPending || remove.isPending || covers.busy;
  const submit = (body: DashboardHighlightAdminCreate) => {
    setErrorKey(null);
    if (isCreate) create.mutate(body);
    else update.mutate(body);
  };

  const header = (
    <Box sx={{ marginBottom: 3 }}>
      <BackLink
        to="/dashboard-highlights"
        label={t("highlightEditor.backToList")}
        testId="highlight.back"
      />
      <Typography variant="H1" sx={{ color: "text.primary", marginTop: 1 }}>
        <WithHalfdot size={11}>
          {t(
            isCreate
              ? "highlightEditor.createTitle"
              : "highlightEditor.editTitle",
          )}
        </WithHalfdot>
      </Typography>
    </Box>
  );

  if (!isCreate && highlight.isPending) {
    return (
      <Screen maxWidth="xl">
        {header}
        <LoadingState />
      </Screen>
    );
  }

  if (!isCreate && highlight.isError) {
    const notFound =
      highlight.error instanceof ApiError &&
      (highlight.error.status === 404 || highlight.error.status === 422);
    return (
      <Screen maxWidth="xl">
        {header}
        {notFound ? (
          <EmptyState message={t("highlightEditor.notFound")} />
        ) : (
          <ErrorState onRetry={() => void highlight.refetch()} />
        )}
      </Screen>
    );
  }

  return (
    <Screen maxWidth="xl">
      {header}
      <DashboardHighlightForm
        key={
          highlight.data
            ? `${highlight.data.id}:${highlight.data.updated_at}`
            : "new"
        }
        initial={highlight.data}
        busy={busy}
        errorKey={errorKey}
        submitLabel={t(
          isCreate ? "highlightEditor.create" : "highlightEditor.save",
        )}
        onSubmit={submit}
        covers={covers}
        actions={
          isCreate ? null : (
            <Button
              color="error"
              variant="text"
              disabled={busy}
              onClick={() => setConfirmingDelete(true)}
              data-testid="highlight.delete"
            >
              {t("highlightEditor.delete")}
            </Button>
          )
        }
      />

      <Dialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
      >
        <DialogTitle>{t("highlightEditor.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("highlightEditor.deleteConfirmBody", {
              title: highlight.data?.title ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => remove.mutate()}
            disabled={busy}
            data-testid="highlight.delete-confirm"
          >
            {t("highlightEditor.deleteConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={savedOpen}
        autoHideDuration={3000}
        onClose={() => setSavedOpen(false)}
        message={t("highlightEditor.saved")}
        data-testid="highlight.saved"
      />
    </Screen>
  );
}
